import { collectMarkableText, findCitationParent, isNoiseTextNode, iterateMarkableTextNodes } from './text-filter'

const CONTEXT_LENGTH = 30

/**
 * Pulls the [textBefore, text, textAfter] triple from a Range for later
 * disambiguation. Operates on noise-filtered "markable" text — the same
 * filter runs at rehydration so offsets line up.
 */
export function captureSelectionContext(range: Range): {
  text: string
  textBefore: string
  textAfter: string
} {
  const containerEl = findCitationParent(range.startContainer)
    ?? (range.startContainer instanceof Element
      ? range.startContainer
      : range.startContainer.parentElement)

  if (!containerEl) {
    return { text: range.toString(), textBefore: '', textAfter: '' }
  }

  const fullText = collectMarkableText(containerEl)
  const startOffset = computeMarkableOffsetWithin(containerEl, range.startContainer, range.startOffset)
  const endOffset = computeMarkableOffsetWithin(containerEl, range.endContainer, range.endOffset)

  if (startOffset < 0 || endOffset < 0 || endOffset <= startOffset) {
    return { text: range.toString(), textBefore: '', textAfter: '' }
  }

  return {
    text: fullText.slice(startOffset, endOffset),
    textBefore: fullText.slice(Math.max(0, startOffset - CONTEXT_LENGTH), startOffset),
    textAfter: fullText.slice(endOffset, endOffset + CONTEXT_LENGTH),
  }
}

// Char offset of (target, targetOffset) indexing into collectMarkableText(root).
export function computeMarkableOffsetWithin(
  root: Element,
  target: Node,
  targetOffset: number,
): number {
  const citationParent = findCitationParent(root) ?? root

  let resolvedTarget: Node = target
  let resolvedOffset = targetOffset

  if (resolvedTarget instanceof Element) {
    const children = resolvedTarget.childNodes
    const atElementEnd = resolvedOffset >= children.length
    const boundaryChild = atElementEnd
      ? children[children.length - 1]
      : children[resolvedOffset]

    if (boundaryChild) {
      if (atElementEnd) {
        let lastText: Text | null = null

        for (const node of iterateMarkableTextNodes(boundaryChild, citationParent)) {
          lastText = node
        }

        if (lastText) {
          resolvedTarget = lastText
          resolvedOffset = lastText.length
        }
      }
      else {
        const [firstText] = iterateMarkableTextNodes(boundaryChild, citationParent)

        if (firstText) {
          resolvedTarget = firstText
          resolvedOffset = 0
        }
      }
    }
  }

  // A noise target (selection started inside a `.reference`) has no offset.
  if (
    resolvedTarget.nodeType === Node.TEXT_NODE
    && isNoiseTextNode(resolvedTarget, citationParent)
  ) {
    return -1
  }

  let offset = 0

  for (const node of iterateMarkableTextNodes(root, citationParent)) {
    if (node === resolvedTarget) {
      const length = node.length
      return offset + Math.max(0, Math.min(resolvedOffset, length))
    }

    offset += node.length
  }

  return -1
}

/**
 * Sort position within the page's reading content: container index is the
 * major key (law text before commentary), markable-text offset the minor.
 * Null when the selection is outside every container — the sorter puts
 * position-less (old) records last.
 */
export function computeMarkingPosition(
  range: Range,
  containerRoots: readonly (Element | null | undefined)[],
): number | null {
  const CONTAINER_STRIDE = 100_000_000

  for (const [index, root] of containerRoots.entries()) {
    if (!root?.contains(range.startContainer)) {
      continue
    }

    const offset = computeMarkableOffsetWithin(root, range.startContainer, range.startOffset)

    if (offset < 0) {
      return null
    }

    return index * CONTAINER_STRIDE + offset
  }

  return null
}

// Same `data-citation-source` scope the copy-with-source pipeline uses, so
// captured offsets are stable regardless of selection nesting.
